import isNil from 'lodash/isNil';
import React, { MutableRefObject, useRef, useState } from 'react';
import isEmail from 'validator/lib/isEmail';
import Button, { ButtonGroup } from '@atlaskit/button';
import { ErrorMessage, Field, Fieldset } from '@atlaskit/form';
import { default as AddIcon } from '@atlaskit/icon/glyph/editor/add';
import Textfield from '@atlaskit/textfield';
import { createAtlassianAccount, fetchAtlassianAccountID } from '@atlassian/temp-proxies';
import { AtlassianEmailUserIdMapping } from '@atlassian/cssxp-types-atlasaccnt';
import { validateDomain } from '../../../account-subscriptions/billing-v2/tabs/billing-contact-tab/src/utils';
import { InfoPanel } from '../../info-panel';
import './add-billing-admins-fields.scss';
import { INVALID_DOMAIN_MESSAGE } from './constants';

interface Props {
  billingAdmins: string[];
  isRequired?: boolean;
  placeholder?: string;
  title?: string;
  helperMessage?: string;
  disableSaveButton: (status: boolean) => void;
  allowToAddBillingAdmin: boolean;
  validDomains?: (string | null | undefined)[];
  isPartnerAccount?: boolean;
}

const inValidCharCode = (admin: string): string | undefined => {
  return admin.split('').find((item) => {
    if (item.charCodeAt(0) < 32 || item.charCodeAt(0) > 126) {
      return item;
    }
  });
};

export const validateEmail = (
  admin: string | undefined,
  validateEmailDomain: ((_email: string) => boolean | undefined) | undefined
) => {
  if (admin) {
    // Invalid characters code filtration needs to be changes in future after discussion . Ref ticket CCPS-647.
    if (!isEmail(admin) || !!inValidCharCode(admin)) return `${admin} must be a valid e-mail address`;
    if (validateEmailDomain && !validateEmailDomain(admin)) {
      return validateEmailDomain(admin) ? undefined : INVALID_DOMAIN_MESSAGE;
    }
  }
};

//nothing
export const AddBillingAdminsFields = ({
  billingAdmins,
  isRequired,
  placeholder,
  disableSaveButton,
  allowToAddBillingAdmin,
  validDomains = [],
  isPartnerAccount = false,
}: Props) => {
  const billingAdminListOnFile = billingAdmins.filter((billingAdmin) => !isNil(billingAdmin));

  const [adminList, setAdminList] = React.useState([...billingAdminListOnFile, '']);

  const [emailAAID, setEmailAAIDList] = useState<AtlassianEmailUserIdMapping>({ email: '', account_id: '' });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<string>('');
  const [requireAtlassianAccount, setRequireAtlassianAccount] = useState<boolean>(false);
  const [accountSignUpResponseID, setAccountSignUpResponseID] = useState<string>('');

  React.useEffect(() => {
    disableSaveButton(adminList.length === 0);
  }, [adminList]);

  // Since setTimeout timer gets lost after component get updated , hence using ref to hold that value.
  const timer: MutableRefObject<number> = useRef(0);
  const debounceDelay = 1000;
  const applicationName = 'wac';

  const debounce = (fn: Function, timeout: number) => {
    return function (args: string) {
      clearTimeout(timer.current);
      //@ts-ignore
      timer.current = setTimeout(() => fn.call(this, args), timeout);
    };
  };

  const updateName = (event: React.FormEvent<HTMLInputElement>, index: number) => {
    const updatedAdminList = [...adminList];
    updatedAdminList[index] = event.currentTarget.value;
    setAdminList(updatedAdminList);
    const _fnDebounce = debounce(async (value: string) => validateBillingAdminEmailID(value), debounceDelay);
    _fnDebounce(event.currentTarget.value);
  };

  const removeAdmin = (index: number) => {
    const updatedAdminList = [...adminList];
    updatedAdminList.splice(index, 1);
    setAdminList(updatedAdminList);
    setEmailAAIDList({ email: '', account_id: '' });
    setAccountSignUpResponseID('');
    resetFlags();
    disableSaveButton(false);
  };

  const domainValidation = (email: string) => {
    const flag = validateDomain(validDomains, email, isPartnerAccount);
    disableSaveButton(!flag);
    return flag;
  };

  const handleRemoveBillingAdmin = (index: number) => {
    removeAdmin(index);
  };

  const addAdmin = () => {
    setAdminList((adminList) => [...adminList, '']);
    if (allowToAddBillingAdmin) disableSaveButton(true);
    setEmailAAIDList({ email: '', account_id: '' });
    setAccountSignUpResponseID('');
  };

  const resetFlags = () => {
    setIsError('');
    setRequireAtlassianAccount(false);
  };

  /**
   * Check if billing admin email is valid .
   * Identity Provider gives AAID linked to email address.
   */
  const validateBillingAdminEmailID = async (email: string) => {
    const payload: { emails: Array<string> } = {
      emails: [email],
    };
    resetFlags();
    if (isEmail(email) && !inValidCharCode(email) && validateDomain(validDomains, email, isPartnerAccount)) {
      setIsLoading(true);
      try {
        const response = await fetchAtlassianAccountID(payload);
        if (!response.length) {
          setEmailAAIDList({ email, account_id: '' });
          disableSaveButton(true);
          setRequireAtlassianAccount(true);
        } else {
          resetFlags();
          disableSaveButton(false);
          setEmailAAIDList({ email, account_id: response[0].account_id });
        }
      } catch (error) {
        resetFlags();
        setIsError(
          isPartnerAccount
            ? 'We couldn’t find the Atlassian Account - try again or contact Partner service desk.'
            : 'Failed to fetch Atlassian Account ID'
        );
        disableSaveButton(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const createAtlassianAccountWithEmail = async (admin: string) => {
    // resetFlags();
    try {
      setIsLoading(true);
      const response = await createAtlassianAccount(admin, applicationName);
      resetFlags();
      disableSaveButton(false);
      setEmailAAIDList({ email: admin, account_id: response.id });
      setAccountSignUpResponseID(response.id);
    } catch (error) {
      setIsError(
        isPartnerAccount
          ? `We're unable to create an Atlassian Account for ${admin}`
          : `Error in creating an Atlassian Account for ${admin}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getAccountSignUpBanner = (admin: string) => {
    const needAtlassianAccountTitle = 'This email is not registered as an Atlassian account';
    const needAtlassianAccountMessage = (
      <div>
        Select <span className="fw-6">Create Atlassian account</span> to register this email address. We’ll send an
        email to <span className="fw-6">{emailAAID.email}</span> so they can verify their email and complete the signup
        process.
        <Button
          appearance="link"
          testId="create-account"
          onClick={() => createAtlassianAccountWithEmail(admin)}
          spacing="none"
          className="create-atlassian-account"
        >
          Create Atlassian account
        </Button>
      </div>
    );
    const createdAtlassianAccountSuccessMessage = `Created Atlassian account for ${emailAAID.email}`;
    if (emailAAID?.email === admin) {
      if (requireAtlassianAccount) {
        return (
          <div className="account-creation-banner mb-20" data-testid="account-creation-banner">
            <br />
            <InfoPanel
              type="warning"
              title={needAtlassianAccountTitle}
              message={needAtlassianAccountMessage}
            ></InfoPanel>
          </div>
        );
      } else if (emailAAID.account_id !== '' && accountSignUpResponseID !== '') {
        return (
          <div className="account-creation-banner mb-20" data-testid="account-creation-banner">
            <br />
            <InfoPanel type="success" message={createdAtlassianAccountSuccessMessage}></InfoPanel>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <>
      <Fieldset>
        {adminList.map((admin, index) => {
          return (
            <div key={index}>
              <div key={index}>
                <Field
                  name={`recipients[${index}]`}
                  defaultValue={admin}
                  label="Email address"
                  isRequired={isRequired}
                  validate={(email) => validateEmail(email, domainValidation)}
                >
                  {({ fieldProps, error }: any) => (
                    <>
                      <div className="add-billing-admins">
                        <Textfield
                          {...fieldProps}
                          testId="billing-admin-email"
                          placeholder={placeholder ?? ''}
                          autoComplete="off"
                          onChange={(event: React.FormEvent<HTMLInputElement>) => {
                            updateName(event, index);
                            fieldProps.onChange(event);
                          }}
                          value={admin}
                        />
                        <ButtonGroup>
                          {!(
                            (
                              adminList.length === 1 &&
                              index === 0 &&
                              admin === ''
                            ) /** to hide remove button very first time */
                          ) && (
                            <Button
                              appearance="link"
                              testId="remove-button"
                              onClick={() => handleRemoveBillingAdmin(index)}
                            >
                              Remove
                            </Button>
                          )}
                        </ButtonGroup>
                      </div>

                      {error && <ErrorMessage>{error}</ErrorMessage>}
                      {isError && index === adminList.length - 1 && (
                        <ErrorMessage testId="error-in-fetching">{isError}</ErrorMessage>
                      )}
                    </>
                  )}
                </Field>
              </div>
              {admin && getAccountSignUpBanner(admin)}
            </div>
          );
        })}
      </Fieldset>
      <div className="btn-add-billing-admin">
        <Button
          appearance="link"
          testId="add-new-admin"
          iconBefore={<AddIcon label="Add icon" size="medium" />}
          onClick={addAdmin}
        >
          Add another email address
        </Button>
      </div>
    </>
  );
};

export default AddBillingAdminsFields;
